import { useEffect, useRef, useState } from 'react'
import { MdWarning } from 'react-icons/md'
import { Link } from 'react-router-dom'

/**
 * Diálogo da baixa manual quando já entrou um Pix SEM VÍNCULO de mesmo valor.
 *
 * O backend recusa a baixa com `ORPHAN_PIX_MATCH` (ver `assertNoOrphanPixMatch`): foi assim
 * que o caixa contou dinheiro duas vezes em agosto/2026. Quem dá baixa vê o aviso, pode ir
 * vincular o Pix, ou registra mesmo assim informando o motivo. Decisão do owner: avisar e
 * pedir justificativa, não bloquear.
 *
 * Fica montado uma vez no layout e é aberto por `requestOrphanJustification`, para as
 * quatro telas que dão baixa não precisarem repetir o modal.
 *
 * É um `<dialog>` aberto com `showModal()` de propósito: a baixa costuma partir de um
 * `ConfirmModal`, que também é `<dialog>` e continua aberto ("Processando...") enquanto a
 * requisição espera esta resposta. Os dois vivem na camada superior do navegador, onde
 * z-index não vale; o que vale é a ordem de abertura, e este abre por último.
 */

type Request = { message: string; resolve: (note: string | null) => void }

let openDialog: ((request: Request) => void) | null = null

/** Abre o diálogo e devolve a justificativa, ou `null` se a pessoa desistiu. */
export function requestOrphanJustification(message: string): Promise<string | null> {
    return new Promise(resolve => {
        if (!openDialog) {
            resolve(null)
            return
        }
        openDialog({ message, resolve })
    })
}

export default function OrphanPixMatchDialogHost() {
    const [request, setRequest] = useState<Request | null>(null)
    const [note, setNote] = useState('')

    useEffect(() => {
        openDialog = r => {
            setNote('')
            setRequest(r)
        }
        return () => {
            openDialog = null
        }
    }, [])

    const dialogRef = useRef<HTMLDialogElement>(null)

    useEffect(() => {
        const dialog = dialogRef.current
        if (!dialog) return
        if (request && !dialog.open) dialog.showModal()
        if (!request && dialog.open) dialog.close()
    }, [request])

    const close = (value: string | null) => {
        request?.resolve(value)
        setRequest(null)
    }
    const valid = note.trim().length >= 5

    return (
        <dialog
            ref={dialogRef}
            className="modal"
            onCancel={e => {
                // Esc: mesmo efeito de "Cancelar", sem deixar a promessa pendurada.
                e.preventDefault()
                close(null)
            }}
        >
            {request && (
                <div className="modal-box max-w-lg flex flex-col gap-4">
                    <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning/15 text-warning">
                            <MdWarning size={22} />
                        </span>
                        <div>
                            <h3 className="font-semibold text-lg">Já entrou um Pix deste valor</h3>
                            <p className="text-sm text-base-content/70 mt-1">{request.message}</p>
                        </div>
                    </div>

                    <Link
                        to="/financial/orphan-payments"
                        target="_blank"
                        className="btn btn-sm btn-secondary w-fit"
                    >
                        Ver pagamentos sem vínculo
                    </Link>

                    <div className="form-control">
                        <label className="label py-1" htmlFor="orphan-pix-match-note">
                            <span className="label-text font-medium">Motivo para registrar mesmo assim</span>
                        </label>
                        <textarea
                            id="orphan-pix-match-note"
                            className="textarea textarea-bordered w-full"
                            rows={3}
                            placeholder="Ex.: o paciente pagou em dinheiro no balcão; o Pix é de outro atendimento"
                            value={note}
                            onChange={e => setNote(e.target.value)}
                        />
                        <span className="text-xs text-base-content/50 mt-1">Fica gravado na cobrança.</span>
                    </div>

                    <div className="modal-action mt-0">
                        <button type="button" className="btn btn-ghost" onClick={() => close(null)}>
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-warning"
                            disabled={!valid}
                            onClick={() => close(note.trim())}
                        >
                            Registrar mesmo assim
                        </button>
                    </div>
                </div>
            )}
            <div className="modal-backdrop" onClick={() => close(null)} />
        </dialog>
    )
}
